"use client";

import React from "react";

/**
 * SONDA DE DIAGNÓSTICO — "Rendered more hooks than during the previous render"
 * (em produção o React minifica a mensagem para "Minified React error #310").
 *
 * ─── Por que existe ────────────────────────────────────────────────────────
 * Esse erro aborta o render da subárvore de forma INTERMITENTE. O Next 16 fixa
 * os handlers `onCaughtError`/`onUncaughtError` do root React
 * (node_modules/next/dist/client/app-index.js) — não há API pra plugar os
 * nossos. E o `onCaughtError` do Next só imprime "The above error occurred in
 * the <X> component", que é a PRIMEIRA linha do componentStack; o resto do
 * caminho (o que identifica o culpado de verdade) é descartado.
 *
 * Este boundary existe só pra guardar o `info.componentStack` INTEIRO antes que
 * o React o jogue fora.
 *
 * ─── O que a caça de 05/08/2026 já descobriu ───────────────────────────────
 * A ocorrência investigada NÃO vem de código nosso: o throw acontece dentro do
 * <Router> do próprio Next (app-router.js:116), logo depois do `use()`
 * condicional de `useActionQueue` (use-action-queue.js:139). É bug do React,
 * corrigido em facebook/react#36911 — que o React empacotado no Next 16.2.9
 * ainda não tem. Como o <Router> fica ACIMA de qualquer error boundary do app,
 * ESTE componente nunca vai capturar aquele caso; o canal que enxerga é o
 * listener de `window.error` em instrumentation-client.ts.
 *
 * Ou seja: a sonda fica de pé pro caso GERAL (ordem de hooks quebrada em
 * componente NOSSO), que é o que ela captura com componentStack completo.
 *
 * ─── Regras de projeto ─────────────────────────────────────────────────────
 *  - EM PRODUÇÃO É INERTE: sem a flag, o export nem é um error boundary — é um
 *    pass-through puro. Nada de interceptar erro de usuário real. (O medo
 *    antigo de "mudar o formato da árvore entre dev e prod" não se aplica:
 *    ambos os caminhos ocupam exatamente um fiber, e a escolha é uma constante
 *    de BUILD — nunca muda em runtime, então não há risco de trocar a ordem de
 *    hooks de ninguém.)
 *  - A SONDA OBSERVA, NÃO CONSERTA: TODO erro que chega aqui é RE-LANÇADO,
 *    inclusive o de ordem de hooks. Não existe mais o "captura, remonta com key
 *    nova e segue". O motivo é o que mais importa neste arquivo:
 *
 *      Não há `app/(app)/error.tsx` no projeto (só o `app/error.tsx`, ACIMA
 *      deste ponto, e o `app/(app)/equipes/error.tsx`, abaixo). Logo esta sonda
 *      é o error boundary MAIS PRÓXIMO de praticamente toda rota do app. Se ela
 *      tratasse o erro, um hook condicional de verdade — num diálogo com
 *      formulário meio preenchido, digamos — apareceria pro dev como "a tela
 *      piscou e o formulário zerou", sem tela de erro nenhuma. O erro que o
 *      time está caçando viraria justamente o mais fácil de não ver.
 *
 *    Re-lançando, o erro segue pro `app/error.tsx` e pro overlay de dev do Next
 *    exatamente como seguiria se esta sonda não existisse — e a gente ainda
 *    fica com o componentStack completo no console. Custo: o Next loga o erro
 *    duas vezes (uma por boundary que o vê). Barulho aceitável.
 *  - Pra capturar o componentStack é preciso UM commit vazio antes de
 *    re-lançar: o `componentDidCatch` (único lugar onde o stack existe) só roda
 *    se o render pós-erro comitar. Por isso o render devolve `null` uma vez e
 *    só então o erro volta a subir.
 *  - ZERO hooks aqui dentro (classe). Uma sonda de bug de hook que usasse hook
 *    seria capaz de causar o próprio bug que investiga.
 */

const PROBE_ENABLED =
  process.env.NODE_ENV !== "production" ||
  process.env.NEXT_PUBLIC_HOOKS_PROBE === "1";

type Props = {
  /** Rótulo do ponto da árvore onde o boundary está (aparece no log). */
  label: string;
  children: React.ReactNode;
};

type State = {
  /** Erro capturado, aguardando o commit em que o componentStack fica legível. */
  pending: Error | null;
  /** Stack já logado: agora o erro precisa voltar a subir a árvore. */
  rethrow: Error | null;
};

/**
 * Mensagens de erro de ordem de hooks. `#310` é a forma minificada de
 * "Rendered more hooks…" no build de produção; `#300` é a de "Rendered fewer
 * hooks…", o outro lado da mesma moeda.
 */
function isHookOrderError(error: unknown): boolean {
  const msg =
    error instanceof Error ? error.message : typeof error === "string" ? error : "";
  return (
    /Rendered (more|fewer) hooks/i.test(msg) ||
    /Minified React error #3(00|10)\b/.test(msg)
  );
}

class HooksProbeBoundaryImpl extends React.Component<Props, State> {
  state: State = { pending: null, rethrow: null };

  /**
   * Só existir já faz o React tratar isto como error boundary EXPLÍCITO — sem
   * isso o Next classifica o erro como não-capturado e nem chama o nosso
   * componentDidCatch com o componentStack. Guardamos o erro e decidimos no
   * render, porque render é o único lugar de onde dá pra RE-LANÇAR e deixar o
   * erro seguir subindo.
   */
  static getDerivedStateFromError(error: Error): Partial<State> {
    return { pending: error };
  }

  /** Único lugar onde o `componentStack` completo existe. */
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (!isHookOrderError(error)) return; // não é o nosso caso: render() re-lança

    // Uma linha só, com marcador fácil de grepar no Playwright.
    // `componentStack` é multi-linha — vai inteiro, sem truncar.
    console.error(
      "[HOOKS-PROBE]",
      `boundary=${this.props.label}`,
      `url=${typeof window !== "undefined" ? window.location.pathname + window.location.search : "?"}`,
      `w=${typeof window !== "undefined" ? window.innerWidth : "?"}`,
      `msg=${error?.message ?? String(error)}`,
      "\n--- componentStack ---",
      info?.componentStack ?? "(vazio)",
      "\n--- errorStack ---",
      error?.stack ?? "(sem stack)",
    );

    // Stack guardado — devolve o erro pra árvore no próximo render. A sonda é
    // só um grampo de escuta: quem decide o que aparece na tela continua sendo
    // o error.tsx do Next. Engolir aqui esconderia o bug caçado.
    this.setState({ pending: null, rethrow: error });
  }

  render() {
    const { pending, rethrow } = this.state;

    // Já logamos o componentStack: o erro volta a subir como se este boundary
    // não existisse.
    if (rethrow) throw rethrow;

    if (pending) {
      // Não é erro de ordem de hooks: não temos nada a capturar, sobe direto.
      if (!isHookOrderError(pending)) throw pending;
      // É o nosso caso: UM commit vazio, só pra destravar o componentDidCatch
      // (que lê o componentStack) — ele re-lança logo em seguida.
      return null;
    }

    return this.props.children;
  }
}

/** Produção sem a flag: nem error boundary é — só repassa os filhos. */
function HooksProbePassthrough({ children }: Props) {
  return <>{children}</>;
}

const HooksProbeBoundary = PROBE_ENABLED
  ? HooksProbeBoundaryImpl
  : HooksProbePassthrough;

export default HooksProbeBoundary;
